import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grow, useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useContext } from 'react'
import RootStore from '../stores/RootStore'
import { useI18n } from '../../shared-lib/i18n/i18n'
import zIndexes from '../theme/zIndexes'
import RectButton from './RectButton'
import TextButton from './TextButton'
import { $BottomMessageButton, $BottomMessageData } from '../stores/BottomMessageHandler'
import CircleIcon from './CircleIcon'
import layout from '../theme/layout'
import icons from '../assets/icons'
import { MuiMarkdown } from 'mui-markdown'

type MessageDialogProps = {
  title?: string
  body?: string
  icon?: $BottomMessageData['icon']
  buttons?: ($BottomMessageButton & { preventModalClose?: boolean })[]
  visible?: boolean
  hideMessage: () => void
  important?: boolean
  bannerImage?: $BottomMessageData['bannerImage']
}

const MessageDialog = (props: MessageDialogProps) => {
  const {
    title,
    body,
    icon,
    buttons,
    visible,
    hideMessage,
    important,
    bannerImage,
  } = props

  const { s } = useI18n()
  const theme = useTheme()

  const _buttons = buttons || [{ title: 'Ok', onPress: () => hideMessage, type: 'TextButton' }]

  return <Dialog
    open={!!visible}
    TransitionComponent={Grow}
    keepMounted
    onClose={!important ? hideMessage : undefined}
    sx={{
      zIndex: zIndexes.dialogs,
      pointerEvents: visible ? 'auto' : 'none',
      '& .MuiDialogActions-root > button': { margin: 0 },
    }}
  >
    <Box sx={{ alignContent: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, minWidth: theme.spacing(45), maxWidth: layout.modalDrawerWidthSmall }}>
      {!!bannerImage && <img src={bannerImage} width={'100%'} alt='banner' style={{ marginTop: theme.spacing(-4) }} />}
      {icon?.iconName && <CircleIcon icon8={icon.iconName} size={'large'} color={icon.color} />}
      {title && <DialogTitle sx={{ textAlign: 'center', pt: 2 }}>{s(title)}</DialogTitle>}
      {body && <DialogContent sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <DialogContentText sx={{ textAlign: 'center', overflowWrap: 'break-word' }}>
          <MuiMarkdown overrides={{}}>{s(body)}</MuiMarkdown>
        </DialogContentText>
      </DialogContent>}

      <DialogActions sx={{ justifyContent: 'center', flexDirection: 'column', px: 4, gap: 2, width: '100%', pb: 0 }}>
        {_buttons?.map(button => {
          const onPress = () => {
            if (button.onPress) button.onPress()
            if (!button.preventModalClose) hideMessage()
          }

          if (button.type === 'TextButton') {
            return <TextButton
              fullWidth
              key={button.title}
              onClick={onPress}
            >
              {s(button.title)}
            </TextButton>
          }

          return <RectButton
            fullWidth
            key={button.title}
            onClick={() => {
              if (button.onPress) button.onPress()
              if (!button.preventModalClose) hideMessage()
            }}
            size='medium'
            variant={!button.type ? 'outlined' : 'contained'}
            color={button.color}
          >
            {s(button.title)}
          </RectButton>
        })}
      </DialogActions>
    </Box>
  </Dialog>
}

export const UiStoreMessageDialog = observer(() => {
  const store = useContext(RootStore)
  const {
    message: { title, body, buttons, visible, important, icon },
    hideMessage,
  } = store.uiStore

  const _buttons = buttons
    ?.map(button => ({
      ...button,
      type: 'RectButton' as const,
      color: button.type,
    }))

  return <MessageDialog
    title={title}
    body={body}
    buttons={_buttons}
    visible={visible}
    hideMessage={hideMessage}
    important={important}
    icon={{
      type: 'circleIcon',
      iconName: icon as keyof typeof icons,
      color: 'primary',
    }}
  />
})

export const BottomMessageDialog = observer(() => {
  const store = useContext(RootStore)
  const {
    messageData: { title, body, actions, icon, bannerImage, canDismiss },
    visible,
    hideBottomMessage,
  } = store.bottomMessageHandler

  const _buttons = (actions || [{ title: 'Ok', onPress: () => hideBottomMessage }]) as $BottomMessageData['actions']

  return <MessageDialog
    title={title}
    body={body}
    icon={icon}
    buttons={_buttons}
    visible={visible}
    hideMessage={hideBottomMessage}
    bannerImage={bannerImage}
    important={!canDismiss}
  />
})